import { Suspense, useState, useCallback, useLayoutEffect, useRef } from "react";
import {
  Bell,
  Settings,
  CheckCircle2,
  Inbox,
  Loader2,
} from "lucide-react";
import { NotificationInboxRow } from "@/components/notifications/NotificationInboxRow.jsx";
import { localizeNotificationModuleLabel } from "@/lib/notifications/notification-inbox-row.js";
import { resolveLocalizedNotificationCopy } from "@/lib/notifications/resolve-localized-notification-copy.js";
import { useNotificationStore } from "@/store";
import { notificationIntegration } from "@/lib/api/client.js";
import { useNotificationNavigation } from "@/hooks/notifications/useNotificationNavigation";
import { Button } from "@repo/ui/primitives/button";
import { useTranslation } from "react-i18next";
import { LATE_I18N_OPTIONS } from "@/lib/i18n.config.js";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@repo/ui/primitives/dropdown-menu";
import { cn } from "@/lib/utils";

const SCROLL_LOAD_THRESHOLD_PX = 80;

function hasAuthToken() {
  const authState = JSON.parse(localStorage.getItem("auth-storage") || "{}");
  return Boolean(authState?.state?.token);
}

function NotificationInboxRows({ notifications, tChrome, language, onSelect }) {
  const { t: tMyWork } = useTranslation("myWork", LATE_I18N_OPTIONS);
  const { t: tEvents } = useTranslation("notificationEvents");
  return notifications.map((n) => {
    const copy = resolveLocalizedNotificationCopy(n, {
      title: n.title,
      message: n.message,
      t: tEvents,
      language,
    });
    return (
    <DropdownMenuItem
      key={n.id}
      onSelect={() => onSelect(n)}
      className={cn(
"group flex items-start gap-2 px-4 py-3 cursor-pointer transition-colors duration-[var(--ds-motion-duration-fast)] motion-reduce:transition-none",
"hover:bg-accent/50 focus:bg-accent/50",
        !n.isRead && "bg-accent/20",
      )}
    >
      <NotificationInboxRow
        className="flex-1"
        row={n}
        title={copy.title}
        message={copy.message}
        language={language}
        moduleLabel={localizeNotificationModuleLabel(n, tMyWork)}
        density="compact"
        showTime
      />
      <div className="flex flex-col items-center gap-1 shrink-0">
        {!n.isRead && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              notificationIntegration.markAsRead(n.id);
            }}
            className="rounded-[var(--ds-radius-control)] p-2 text-muted-foreground transition-colors duration-[var(--ds-motion-duration-fast)] hover:bg-accent hover:text-accent-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 motion-reduce:transition-none"
            aria-label={tChrome("notifications.markAsRead")}
            title={tChrome("notifications.markAsRead")}
          >
            <CheckCircle2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </DropdownMenuItem>
    );
  });
}

function tryLoadMoreFromScroll(el) {
  if (!el) return;
  const nearBottom =
    el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_LOAD_THRESHOLD_PX;
  if (!nearBottom) return;
  const { hasMore, isLoading, isLoadingMore } = useNotificationStore.getState();
  if (hasMore && !isLoading && !isLoadingMore) {
    notificationIntegration.loadMoreNotifications();
  }
}

export function NotificationBell() {
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const notifications = useNotificationStore((s) => s.notifications);
  const total = useNotificationStore((s) => s.total);
  const hasMore = useNotificationStore((s) => s.hasMore);
  const isLoading = useNotificationStore((s) => s.isLoading);
  const isLoadingMore = useNotificationStore((s) => s.isLoadingMore);
  const { handleNotificationClick } = useNotificationNavigation();
  const { t, i18n } = useTranslation("header");

  const [open, setOpen] = useState(false);
  const scrollRef = useRef(null);

  const handleScroll = useCallback(() => {
    tryLoadMoreFromScroll(scrollRef.current);
  }, []);

  const handleOpenChange = useCallback(
    (nextOpen) => {
      setOpen(nextOpen);
      if (nextOpen && hasAuthToken()) {
        notificationIntegration.fetchNotifications(1, false);
      }
    },
    [],
  );

  useLayoutEffect(() => {
    if (!open || !hasMore) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;

    const { isLoading, isLoadingMore } = useNotificationStore.getState();
    if (isLoading || isLoadingMore) return undefined;

    const fillsViewport =
      el.scrollHeight <= el.clientHeight + SCROLL_LOAD_THRESHOLD_PX;
    if (fillsViewport) {
      notificationIntegration.loadMoreNotifications();
    } else {
      tryLoadMoreFromScroll(el);
    }

    return undefined;
  }, [open, hasMore, notifications.length, isLoading, isLoadingMore]);

  const badge = unreadCount > 0 ? (unreadCount > 99 ? "99+" : String(unreadCount)) : null;
  const showLoadingIndicator = isLoading || isLoadingMore;

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-[var(--ds-radius-control)] text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground motion-reduce:transition-none"
          aria-label={t("notifications.open", { count: unreadCount, defaultValue: "Open notifications" })}
        >
          <Bell className="size-6" />
          {badge && (
            <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
              {badge}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="flex max-h-[min(600px,var(--radix-dropdown-menu-content-available-height))] w-[min(420px,calc(100vw-1rem))] flex-col overflow-hidden p-0"
      >
        <div className="z-10 shrink-0 border-b border-border-subtle bg-popover">
          <DropdownMenuLabel className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              <span className="font-semibold">{t("notifications.title")}</span>
              {unreadCount > 0 && (
                <span className="ml-1 rounded-[var(--ds-radius-pill)] bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
                  {badge}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => notificationIntegration.markAllAsRead()}
                className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
              >
                {t("notifications.markAllAsRead")}
              </button>
            )}
          </DropdownMenuLabel>
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="custom-scrollbar min-h-0 flex-1 overflow-y-auto"
        >
          {notifications.length === 0 && !isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="mb-4 rounded-[var(--ds-radius-pill)] bg-muted p-4">
                <Inbox className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">
                {t("notifications.empty")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("notifications.emptyDescription")}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              <Suspense
                fallback={
                  <div className="flex items-center justify-center gap-2 py-6 text-xs text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                }
              >
                <NotificationInboxRows
                  notifications={notifications}
                  tChrome={t}
                  language={i18n.language}
                  onSelect={handleNotificationClick}
                />
              </Suspense>

              {showLoadingIndicator && (
                <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{t("notifications.loadingMore")}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center border-t border-border-subtle bg-popover p-2">
          {notifications.length > 0 && (
            <span className="text-xs text-muted-foreground px-2">
              {t("notifications.loadedCount", {
                loaded: notifications.length,
                total: total || notifications.length,
              })}
            </span>
          )}
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="ml-auto size-[var(--ds-density-icon-button-size)] rounded-[var(--ds-radius-control)]"
          >
            <Link
              to="/platform/communications/notification-policies"
              aria-label={t("settings.notifications.open", { defaultValue: "Open notification settings" })}
              title={t("settings.notifications.open", { defaultValue: "Open notification settings" })}
            >
              <Settings className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
